import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPause, faPlay, faTimes } from '@fortawesome/free-solid-svg-icons';
import { useBreadcrumb } from '../../../contexts/BreadcrumbContext';
import {
  checkWeightValue,
  dedupeMeasurementsByDateTimeKeepLatest,
  extractWallClockDateYmd,
  formatMeasurementTableDate,
  formatMeasurementTableTime,
  getBmiCategoryStatus,
  BMI_CATEGORY,
  getBmiTableValueClass,
  getMeasurementValueClass,
  getMeasurementWallClockDateYmd,
  getMeasurementWallClockTimeHms,
  kgToLb,
  lbToKg,
  parseWallClockDateLocal
} from '../../../utils/measurementUtils';
import measurementService from '../../../services/measurementService';
import patientService from '../../../services/patientService';
import { toast } from 'react-toastify';
import AddWForm from './AddWForm';
import AddFollowUpModal from '../../../components/AddFollowUpModal/AddFollowUpModal';
import VitalsTabs from '../../../components/MeasurementData/VitalsTabs/VitalsTabs';
import EmailModal from '../../../components/EmailModal/EmailModal';
import { sendReportEmail } from '../../../utils/reportEmailSend';
import { downloadBlob, printElement, toCsv } from '../../../utils/reportExportUtils';
import { exportWeightTablesPdf } from '../../../utils/measurementTablePdfExport';
import {
  findPeriodByName,
  hasAnyScheduleTargets,
  normalizePeriodName,
  resolvePeriodNameByIdOrLabel,
  resolveDisplayScheduleTargets,
  getMeasurementEpochMs
} from '../../../utils/scheduleTargetUtils';
import {
  PATIENT_SCHEDULE_TARGET_PREF_EVENT,
  getUsePatientScheduleTargetsAtMeasurementMs,
  getUsePatientScheduleTargetsPreference
} from '../../../utils/patientScheduleTargetPreference.js';
import { formatPhoneForDisplay } from '../../../utils/phoneUtils';
import {
  canAddPracticeVital,
  MEASURED_TYPE_IDS,
  normalizePracticeMonitoringTypes,
  PRACTICE_VITAL_ADD_DISABLED_TITLE
} from '../../../utils/practiceMonitoringUtils';
import {
  DEFAULT_MEASUREMENT_QUERY,
  applyMeasurementFromDateChange,
  getMeasurementQueryMaxDate,
  isFromToIncomplete,
  isMeasurementQueryDirty,
  patchOnQueryModeChange,
  sanitizeMeasurementToDate,
} from '../../../utils/queryPeriodFilters';
import AppDateInput from '../../../components/Common/AppDateInput/AppDateInput';
import { useMeasurementSessionOptional } from '../MeasurementControlsBar';
import {
  PERMANENT_END_TIMES,
  PERMANENT_START_TIMES,
  determinePeriodFromTime,
  getPermanentPeriodDisplay
} from '../../../utils/measurementPeriodUtils';
import './Weight.css';
import '../MeasurementShell.css';

const Weight = ({ practiceId, patientId, practiceIsLocked = false, practiceMonitoringTypes = null }) => {
  const { updateBreadcrumbs } = useBreadcrumb();
  const location = useLocation();
  const session = useMeasurementSessionOptional();
  const useSharedControls = Boolean(session);

  const [localTimer, setLocalTimer] = useState(0);
  const [localIsRunning, setLocalIsRunning] = useState(false);
  const timer = useSharedControls ? session.timer : localTimer;
  const setTimer = useSharedControls ? session.setTimer : setLocalTimer;
  const isRunning = useSharedControls ? session.isRunning : localIsRunning;
  const setIsRunning = useSharedControls ? session.setIsRunning : setLocalIsRunning;
  const [queryType, setQueryType] = useState(DEFAULT_MEASUREMENT_QUERY.queryType);
  const [dateRange, setDateRange] = useState(DEFAULT_MEASUREMENT_QUERY.dateRange);
  const [fromDate, setFromDate] = useState(DEFAULT_MEASUREMENT_QUERY.fromDate);
  const [toDate, setToDate] = useState(DEFAULT_MEASUREMENT_QUERY.toDate);
  const [sortBy, setSortBy] = useState(DEFAULT_MEASUREMENT_QUERY.sortBy);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showFollowUpModal, setShowFollowUpModal] = useState(false);
  const [patientData, setPatientData] = useState(null);
  const [measurements, setMeasurements] = useState([]);
  const [scheduleTargets, setScheduleTargets] = useState(null);
  const [scheduleTargetSources, setScheduleTargetSources] = useState(null);
  const [targetTimelineRev, setTargetTimelineRev] = useState(0);
  const [loading, setLoading] = useState(false);
  const [queryLoading, setQueryLoading] = useState(false);
  const [hasFetchedMeasurements, setHasFetchedMeasurements] = useState(false);
  const [hasFetchedPatientData, setHasFetchedPatientData] = useState(false);
  const [hasFetchedScheduleTargets, setHasFetchedScheduleTargets] = useState(false);
  const patientVitals = patientData?.patient?.vitals || '';
  const resolvedPracticeTypes = useMemo(() => {
    if (Array.isArray(practiceMonitoringTypes)) return practiceMonitoringTypes;
    return normalizePracticeMonitoringTypes(patientData?.practice_monitoring_types);
  }, [practiceMonitoringTypes, patientData?.practice_monitoring_types]);
  const canAddThisVital = canAddPracticeVital({
    practiceIsLocked,
    practiceMonitoringTypes: resolvedPracticeTypes,
    typeId: MEASURED_TYPE_IDS.WT
  });
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailSending, setEmailSending] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [hoverPreview, setHoverPreview] = useState(null);
  const initialized = useRef(false);
  const hoverHideTimerRef = useRef(null);
  const queryStateBeforeAddRef = useRef(null);
  const exportMenuRef = useRef(null);

  useEffect(() => {
    if (practiceIsLocked) {
      setShowAddModal(false);
      setShowFollowUpModal(false);
      setIsRunning(false);
      setTimer(0);
    }
  }, [practiceIsLocked]);

  useEffect(() => {
    if (!canAddThisVital) setShowAddModal(false);
  }, [canAddThisVital]);

  // Fetch patient data and schedule targets on mount
  useEffect(() => {
    if (practiceId && patientId) {
      setHasFetchedPatientData(false);
      setHasFetchedScheduleTargets(false);
      setHasFetchedMeasurements(false);
      setScheduleTargetSources(null);
      if (!initialized.current) {
        if (!practiceIsLocked) {
          setIsRunning(true);
        }
        initialized.current = true;
      }
      fetchPatientData();
      fetchScheduleTargets();
    }
    return () => {
      initialized.current = false;
    };
  }, [practiceId, patientId]);

  // Fetch measurements on initial load / patient change only (filters apply via Query button).
  useEffect(() => {
    if (practiceId && patientId) {
      fetchMeasurements({ silent: true });
    }
  }, [practiceId, patientId]);

  // Timer effect (local only — shared timer lives in MeasurementControlsBar)
  useEffect(() => {
    if (useSharedControls) return undefined;
    let interval = null;
    if (isRunning) {
      interval = setInterval(() => {
        setTimer(prev => prev + 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isRunning, useSharedControls]);

  const formatTimer = (totalSeconds) => {
    const mins = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    return `${mins.toString().padStart(2, '0')} : ${secs.toString().padStart(2, '0')}`;
  };

  const fetchPatientData = async () => {
    const cacheKey = `measurement:patient:${practiceId}:${patientId}`;
    const cachedRaw = sessionStorage.getItem(cacheKey);
    if (cachedRaw) {
      try {
        const cached = JSON.parse(cachedRaw);
        if (cached && typeof cached === 'object') setPatientData(cached);
      } catch {
        // ignore
      }
    }
    try {
      const response = await patientService.getPatientDetailsFast(practiceId, patientId);
      if (response.data.success) {
        const next = response.data.data;
        setPatientData(next);
        sessionStorage.setItem(cacheKey, JSON.stringify(next));
      }
    } catch (error) {
      console.error('Error fetching patient data:', error);
    } finally {
      setHasFetchedPatientData(true);
    }
  };

  const fetchScheduleTargets = async () => {
    try {
      const sources = await patientService.getScheduleTargetSourcesForPatient(practiceId, patientId);
      setScheduleTargetSources(sources);
      const usePatientTargets = getUsePatientScheduleTargetsPreference(practiceId, patientId);
      const effective = resolveDisplayScheduleTargets({
        practiceData: sources.practiceData,
        patientData: sources.patientData,
        usePatientTargets
      });
      setScheduleTargets(hasAnyScheduleTargets(effective) ? effective : sources.practiceData);
    } catch (error) {
      console.error('Error fetching schedule targets:', error);
    } finally {
      setHasFetchedScheduleTargets(true);
    }
  };

  useEffect(() => {
    if (!practiceId || !patientId) return undefined;
    const onPref = (ev) => {
      const d = ev?.detail;
      if (String(d?.practiceId) !== String(practiceId) || String(d?.patientId) !== String(patientId)) return;
      setTargetTimelineRev((n) => n + 1);
      fetchScheduleTargets();
    };
    window.addEventListener(PATIENT_SCHEDULE_TARGET_PREF_EVENT, onPref);
    return () => window.removeEventListener(PATIENT_SCHEDULE_TARGET_PREF_EVENT, onPref);
  }, [practiceId, patientId]);

  const fetchMeasurements = async ({ silent = false } = {}) => {
    try {
      let fromDateParam, toDateParam;
      const isAllHistory = queryType === 'dateRange' && String(dateRange).toUpperCase() === 'ALL';

      if (queryType === 'dateRange') {
        if (!isAllHistory) {
          const days = parseInt(dateRange.replace(/\D/g, ''), 10) || 30;
          const endDate = new Date();
          const startDate = new Date();
          startDate.setDate(startDate.getDate() - days);
          fromDateParam = startDate.toISOString().split('T')[0];
          toDateParam = endDate.toISOString().split('T')[0];
        }
      } else {
        // Inputs are already YYYY-MM-DD from type="date"
        if (fromDate && toDate) {
          fromDateParam = fromDate;
          toDateParam = toDate;
        }
      }

      if (!isAllHistory && !(fromDateParam && toDateParam)) {
        return;
      }

      const queryCacheKey = `measurement:wt:${practiceId}:${patientId}:${queryType}:${dateRange}:${fromDateParam || ''}:${toDateParam || ''}:${sortBy}`;
      let hadCachedData = false;
      const cachedRaw = sessionStorage.getItem(queryCacheKey);
      if (cachedRaw) {
        try {
          const cached = JSON.parse(cachedRaw);
          if (Array.isArray(cached)) {
            setMeasurements(cached);
            setHasFetchedMeasurements(true);
            hadCachedData = true;
          }
        } catch {
          // ignore
        }
      }
      if (!silent || !hadCachedData) {
        setQueryLoading(true);
      }
      const sortKeyMap = {
        '1 Day Average': 'daily_average',
        'Weekly average': 'weekly_average',
        '14 Days Average': 'weekly_average',
        'Monthly average': 'monthly_average',
        '3 Months Average': 'monthly_average',
        'All': 'date',
        'Date': 'date',
        'Daily average': 'daily_average'
      };
      const response = await measurementService.getWeightByDateRange(
        practiceId,
        patientId,
        isAllHistory ? undefined : fromDateParam,
        isAllHistory ? undefined : toDateParam,
        sortKeyMap[sortBy] || 'weekly_average'
      );

      if (response.data?.success) {
        const next = response.data.data || [];
        setMeasurements((prev) => (JSON.stringify(prev || []) === JSON.stringify(next) ? prev : next));
        sessionStorage.setItem(queryCacheKey, JSON.stringify(next));
        setLoading(false);
      }
    } catch (error) {
      console.error('Error fetching measurements:', error);
      if (error.response?.status === 404) {
        console.log('Measurement API endpoint not yet available');
        setMeasurements([]);
      } else {
        toast.error('Failed to load measurements');
        setMeasurements([]);
      }
    } finally {
      setQueryLoading(false);
      setLoading(false);
      setHasFetchedMeasurements(true);
    }
  };

  const isQueryDisabled = queryLoading || isFromToIncomplete(queryType, fromDate, toDate);
  const isResetDisabled = queryLoading || !isMeasurementQueryDirty({
    queryType, dateRange, fromDate, toDate, sortBy,
  });

  const handleQueryTypeChange = (nextType) => {
    const patch = patchOnQueryModeChange(nextType);
    setQueryType(patch.queryType);
    if (Object.prototype.hasOwnProperty.call(patch, 'fromDate')) {
      setFromDate(patch.fromDate);
      setToDate(patch.toDate);
    }
  };

  const handleQuery = () => {
    if (isFromToIncomplete(queryType, fromDate, toDate)) {
      toast.warning('Please select both start and end dates');
      return;
    }
    
    fetchMeasurements({ silent: false });
  };

  const handleReset = () => {
    if (isResetDisabled) return;
    setQueryType(DEFAULT_MEASUREMENT_QUERY.queryType);
    setDateRange(DEFAULT_MEASUREMENT_QUERY.dateRange);
    setFromDate(DEFAULT_MEASUREMENT_QUERY.fromDate);
    setToDate(DEFAULT_MEASUREMENT_QUERY.toDate);
    setSortBy(DEFAULT_MEASUREMENT_QUERY.sortBy);
  };

  const handleAddDataSuccess = (newReading = null) => {
    const prevQuery = queryStateBeforeAddRef.current;
    if (prevQuery) {
      setQueryType(prevQuery.queryType);
      setDateRange(prevQuery.dateRange);
      setFromDate(prevQuery.fromDate);
      setToDate(prevQuery.toDate);
    }

    const selectedDate = newReading?.measurement_date;
    let shouldInsertOptimistically = false;
    if (selectedDate) {
      const selected = new Date(selectedDate);
      selected.setHours(0, 0, 0, 0);
      if (queryType === 'from' && fromDate && toDate) {
        const from = new Date(fromDate);
        const to = new Date(toDate);
        from.setHours(0, 0, 0, 0);
        to.setHours(0, 0, 0, 0);
        shouldInsertOptimistically = selected >= from && selected <= to;
      } else if (String(dateRange).toUpperCase() === 'ALL') {
        shouldInsertOptimistically = true;
      } else {
        const days = parseInt(dateRange.replace(/\D/g, ''), 10) || 30;
        const end = new Date();
        end.setHours(0, 0, 0, 0);
        const start = new Date(end);
        start.setDate(start.getDate() - days);
        shouldInsertOptimistically = selected >= start && selected <= end;
      }
    }

    if (newReading && selectedDate && newReading.measurement_time && shouldInsertOptimistically) {
      const timeFull = `${newReading.measurement_time}:00`;
      const weightInKg = lbToKg(newReading.weight);
      const optimisticMeasurement = {
        id: newReading.id || `local-${selectedDate}-${newReading.measurement_time}-${Date.now()}`,
        weight: Number.isFinite(weightInKg) ? weightInKg : null,
        fat: newReading.body_fat ?? null,
        body_fat: newReading.body_fat ?? null,
        bmi: newReading.bmi ?? null,
        period_name: newReading.period_name || null,
        note: newReading.note || null,
        measurement_date: selectedDate,
        measurement_time: newReading.measurement_time,
        measure_date_time: `${selectedDate} ${timeFull}`,
        measure_new_date_time: `${selectedDate}T${timeFull}`
      };
      setMeasurements((prev) => [optimisticMeasurement, ...(prev || [])]);
    }

    // Keep current query period/date filters unchanged; refresh in background.
    fetchMeasurements({ silent: false });
  };

  const handleOpenAddModal = () => {
    if (!canAddThisVital) return;
    queryStateBeforeAddRef.current = {
      queryType,
      dateRange,
      fromDate,
      toDate
    };
    setShowAddModal(true);
  };

  const formatDate = (dateStr) => formatMeasurementTableDate(dateStr);

  const formatDateIso = (dateStr) => extractWallClockDateYmd(dateStr);

  const formatTime = (timeString) => formatMeasurementTableTime(timeString);

  const resolvePeriodByTime = (timeString, periods) =>
    determinePeriodFromTime(
      timeString,
      periods,
      PERMANENT_START_TIMES.weight,
      PERMANENT_END_TIMES.weight
    ) || '';

  const resolveTargetsBundleAtMs = useCallback(
    (ms) => {
      if (!scheduleTargetSources) return null;
      const usePatient = getUsePatientScheduleTargetsAtMeasurementMs(practiceId, patientId, ms);
      return resolveDisplayScheduleTargets({
        practiceData: scheduleTargetSources.practiceData,
        patientData: scheduleTargetSources.patientData,
        usePatientTargets: usePatient
      });
    },
    [scheduleTargetSources, practiceId, patientId, targetTimelineRev]
  );

  // Get period names from schedule targets
  const periodNames = useMemo(() => {
    const source = !scheduleTargets?.weightTimePeriod
      ? ['Morning', 'Afternoon', 'Evening']
      : scheduleTargets.weightTimePeriod.map((p) => p?.name).filter(Boolean);
    const seen = new Set();
    return source.filter((name) => {
      const key = normalizePeriodName(name);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [scheduleTargets]);

  const getPeriodColor = () => '#F8FAFC';

  // Get period info (including time ranges) from schedule targets
  const getPeriodInfo = (periodName) => {
    if (!scheduleTargets?.weightTimePeriod) {
      return null;
    }
    
    const period = findPeriodByName(scheduleTargets.weightTimePeriod, periodName);
    
    if (!period) return null;
    
    const display = getPermanentPeriodDisplay(
      periodName,
      PERMANENT_START_TIMES.weight,
      PERMANENT_END_TIMES.weight,
      period
    );

    return {
      name: period.name,
      startTime: display.startTime,
      endTime: display.endTime,
      startTimeDisplay: display.startTimeDisplay,
      endTimeDisplay: display.endTimeDisplay
    };
  };

  // Get target for weight
  const getWeightTarget = (bundleOverride) => {
    const bundle = bundleOverride || scheduleTargets;
    if (!bundle?.weightTargetRange) {
      return { weightMin: 66, weightMax: 220, bodyFatMin: 14, bodyFatMax: 27 };
    }
    return bundle.weightTargetRange;
  };

  const effectiveMeasurements = useMemo(
    () => dedupeMeasurementsByDateTimeKeepLatest(measurements),
    [measurements]
  );

  // Group measurements by date
  const groupedMeasurements = useMemo(() => {
    const grouped = {};
    
    const sortedMeasurements = [...effectiveMeasurements].sort((a, b) => {
      const dateA = getMeasurementWallClockDateYmd(a);
      const dateB = getMeasurementWallClockDateYmd(b);
      
      if (dateA !== dateB) {
        return dateB.localeCompare(dateA);
      }
      
      const timeA = getMeasurementWallClockTimeHms(a) || '00:00:00';
      const timeB = getMeasurementWallClockTimeHms(b) || '00:00:00';
      return timeB.localeCompare(timeA);
    });

    // Deduplicate exact duplicates only:
    // same date+time + same reading values => keep latest.
    // same date+time + different values => keep both.
    const uniqueByDateTime = new Map();
    sortedMeasurements.forEach((m) => {
      const date = getMeasurementWallClockDateYmd(m);
      const time = (getMeasurementWallClockTimeHms(m) || '').slice(0, 5);
      const key = `${date}|${time}|${m.weight ?? ''}|${m.fat ?? m.body_fat ?? ''}|${m.bmi ?? ''}`;
      if (!uniqueByDateTime.has(key)) {
        uniqueByDateTime.set(key, m);
      }
    });
    
    Array.from(uniqueByDateTime.values()).forEach(measurement => {
      const date = getMeasurementWallClockDateYmd(measurement);
      if (!date) return;
      
      if (!grouped[date]) {
        grouped[date] = [];
      }
      
      grouped[date].push(measurement);
    });
    
    return grouped;
  }, [effectiveMeasurements]);

  const sortedDates = useMemo(() => {
    return Object.keys(groupedMeasurements).sort((a, b) => b.localeCompare(a));
  }, [groupedMeasurements]);

  const dateBuckets = useMemo(() => {
    if (sortedDates.length === 0) return [];

    if (sortBy === 'All' || sortBy === 'Date') {
      return [{ label: 'All Dates', dates: sortedDates }];
    }

    const daysPerBucket = {
      '1 Day Average': 1,
      'Weekly average': 7,
      '14 Days Average': 14,
      'Monthly average': 30,
      '3 Months Average': 90,
      'Daily average': 1
    }[sortBy] || 7;

    const newest = parseWallClockDateLocal(sortedDates[0]);
    if (!newest) return [{ label: 'All Dates', dates: sortedDates }];
    newest.setHours(0, 0, 0, 0);
    const msPerDay = 24 * 60 * 60 * 1000;

    const buckets = new Map();
    sortedDates.forEach((dateStr) => {
      const d = parseWallClockDateLocal(dateStr);
      if (!d) return;
      d.setHours(0, 0, 0, 0);
      const diffDays = Math.floor((newest - d) / msPerDay);
      const idx = Math.floor(diffDays / daysPerBucket);
      if (!buckets.has(idx)) buckets.set(idx, []);
      buckets.get(idx).push(dateStr);
    });

    return Array.from(buckets.entries())
      .sort((a, b) => a[0] - b[0])
      .map(([idx, dates]) => {
        const sorted = [...dates].sort((a, b) => b.localeCompare(a));
        const start = sorted[sorted.length - 1];
        const end = sorted[0];
        return {
          id: idx,
          label: `Date Range : ${formatDateIso(start)} ~ ${formatDateIso(end)}`,
          dates: sorted
        };
      });
  }, [sortedDates, sortBy]);

  // Calculate statistics for a period
  const calculatePeriodStats = (allMeasurements, periodName) => {
    const normalizedPeriod = normalizePeriodName(periodName);
    const periodMeasurements = allMeasurements.filter(m => {
      const ms = getMeasurementEpochMs(m);
      const bundle = resolveTargetsBundleAtMs(ms) || scheduleTargets;
      const periods = Array.isArray(bundle?.weightTimePeriod) ? bundle.weightTimePeriod : [];
      const periodLabel = resolvePeriodNameByIdOrLabel(periods, m) || m.period_name || m.period;
      let resolved = periodLabel;
      if (!resolved) {
        const mTime = getMeasurementWallClockTimeHms(m);
        if (mTime) resolved = resolvePeriodByTime(mTime, periods);
      }
      if (!resolved) resolved = 'Unknown';
      return normalizePeriodName(resolved) === normalizedPeriod;
    });

    if (!periodMeasurements || periodMeasurements.length === 0) {
      return { 
        avg: { weight: null, fat: null, bmi: null }, 
        max: { weight: null, fat: null, bmi: null }, 
        min: { weight: null, fat: null, bmi: null }, 
        count: 0, 
        above: { weight: 0, fat: 0, bmi: 0 }, 
        below: { weight: 0, fat: 0, bmi: 0 }, 
        within: { weight: 0, fat: 0, bmi: 0 } 
      };
    }

    const weightValues = periodMeasurements.map(m => getWeightLb(m)).filter(v => v != null && !isNaN(v));
    
    const fatValues = periodMeasurements.map(m => parseFloat(m.fat || m.body_fat)).filter(v => !isNaN(v));
    const bmiValues = periodMeasurements.map(m => parseFloat(m.bmi)).filter(v => !isNaN(v));

    const weightAvg = weightValues.length > 0 ? weightValues.reduce((a, b) => a + b, 0) / weightValues.length : null;
    const weightMax = weightValues.length > 0 ? Math.max(...weightValues) : null;
    const weightMin = weightValues.length > 0 ? Math.min(...weightValues) : null;
    
    const fatAvg = fatValues.length > 0 ? fatValues.reduce((a, b) => a + b, 0) / fatValues.length : null;
    const fatMax = fatValues.length > 0 ? Math.max(...fatValues) : null;
    const fatMin = fatValues.length > 0 ? Math.min(...fatValues) : null;
    
    const bmiAvg = bmiValues.length > 0 ? bmiValues.reduce((a, b) => a + b, 0) / bmiValues.length : null;
    const bmiMax = bmiValues.length > 0 ? Math.max(...bmiValues) : null;
    const bmiMin = bmiValues.length > 0 ? Math.min(...bmiValues) : null;

    let aboveWeight = 0, belowWeight = 0, withinWeight = 0;
    let aboveFat = 0, belowFat = 0, withinFat = 0;
    let aboveBmi = 0, belowBmi = 0, withinBmi = 0;
    
    periodMeasurements.forEach(m => {
      const bundle = resolveTargetsBundleAtMs(getMeasurementEpochMs(m)) || scheduleTargets;
      const target = getWeightTarget(bundle);
      const weightKg = m.weight;
      const weightLb = weightKg ? kgToLb(weightKg) : null;
      const fat = m.fat || m.body_fat;
      const bmi = m.bmi;
      
      if (weightLb != null) {
        const status = checkWeightValue(m, target);
        if (status === 'high') aboveWeight++;
        else if (status === 'low') belowWeight++;
        else withinWeight++;
      }
      
      if (fat != null) {
        const fatValue = parseFloat(fat);
        const fatMin = target.bodyFatMin || 14;
        const fatMax = target.bodyFatMax || 27;
        if (fatValue > fatMax) aboveFat++;
        else if (fatValue < fatMin) belowFat++;
        else withinFat++;
      }
      
      if (bmi != null) {
        const bmiValue = parseFloat(bmi);
        const bmiNormal = target.bmiNormal || 18.5;
        const bmiObese = target.bmiObese || 30;
        if (bmiValue >= bmiObese) aboveBmi++;
        else if (bmiValue < bmiNormal) belowBmi++;
        else withinBmi++;
      }
    });

    return {
      avg: { weight: weightAvg, fat: fatAvg, bmi: bmiAvg },
      max: { weight: weightMax, fat: fatMax, bmi: bmiMax },
      min: { weight: weightMin, fat: fatMin, bmi: bmiMin },
      count: periodMeasurements.length,
      above: { weight: aboveWeight, fat: aboveFat, bmi: aboveBmi },
      below: { weight: belowWeight, fat: belowFat, bmi: belowBmi },
      within: { weight: withinWeight, fat: withinFat, bmi: withinBmi }
    };
  };

  // Render all readings for one period/date inside same cells
  const renderPeriodMeasurementsCells = (periodMeasurements, periodName) => {
    if (!periodMeasurements || periodMeasurements.length === 0) {
      return (
        <>
          <td></td>
          <td></td>
          <td></td>
          <td></td>
        </>
      );
    }
    
    const readings = [...periodMeasurements].sort((a, b) => {
      const timeA = getMeasurementWallClockTimeHms(a) || '00:00:00';
      const timeB = getMeasurementWallClockTimeHms(b) || '00:00:00';
      return timeB.localeCompare(timeA);
    });

    return (
      <>
        <td>
          <div className="w-cell-stack">
            {readings.map((measurement, index) => {
              const bundle =
                resolveTargetsBundleAtMs(getMeasurementEpochMs(measurement)) || scheduleTargets;
              const target = getWeightTarget(bundle);
              const weightLb = getWeightLb(measurement);
              const weightStatus = weightLb != null ? checkWeightValue(measurement, target) : 'normal';
              const weightClass = getMeasurementValueClass(weightStatus);
              return (
                <div key={`${measurement.id || index}-weight`} className={weightClass}>
                  {weightLb != null ? weightLb : ''}
                </div>
              );
            })}
          </div>
        </td>
        <td>
          <div className="w-cell-stack">
            {readings.map((measurement, index) => {
              const bundle =
                resolveTargetsBundleAtMs(getMeasurementEpochMs(measurement)) || scheduleTargets;
              const target = getWeightTarget(bundle);
              const fat = measurement.fat || measurement.body_fat;
              let fatClass = 'value-normal';
              if (fat != null) {
                const fatValue = parseFloat(fat);
                const fatMin = target.bodyFatMin || 14;
                const fatMax = target.bodyFatMax || 27;
                if (fatValue > fatMax) fatClass = 'value-high';
                else if (fatValue < fatMin) fatClass = 'value-low';
              }
              return (
                <div key={`${measurement.id || index}-fat`} className={fatClass}>
                  {fat ? parseFloat(fat).toFixed(1) : ''}
                </div>
              );
            })}
          </div>
        </td>
        <td>
          <div className="w-cell-stack">
            {readings.map((measurement, index) => {
              const bundle =
                resolveTargetsBundleAtMs(getMeasurementEpochMs(measurement)) || scheduleTargets;
              const target = getWeightTarget(bundle);
              const bmi = measurement.bmi;
              let bmiClass = 'value-normal';
              if (bmi != null) {
                bmiClass = getBmiTableValueClass(
                  getBmiCategoryStatus(bmi, {
                    bmiNormal: target.bmiNormal || 18.5,
                    bmiOverweight: target.bmiOverweight || 25,
                    bmiObese: target.bmiObese || 30
                  })
                );
              }
              return (
                <div key={`${measurement.id || index}-bmi`} className={bmiClass}>
                  {bmi ? parseFloat(bmi).toFixed(1) : ''}
                </div>
              );
            })}
          </div>
        </td>
        <td>
          <div className="w-cell-stack">
            {readings.map((measurement, index) => {
              const measureTime = getMeasurementWallClockTimeHms(measurement);
              const formattedTime = measureTime ? formatTime(measureTime) : '';
              return <div key={`${measurement.id || index}-time`} className="w-time-text">{formattedTime}</div>;
            })}
          </div>
        </td>
      </>
    );
  };

  const formatPercentCount = (count, total) => {
    if (!total) return `0% (${count})`;
    const pct = Math.round((count / total) * 100);
    return `${pct}% (${count})`;
  };

  const calculateOverallTargetStats = () => {
    const stats = {
      above: { weight: 0, bmi: 0, fat: 0 },
      below: { weight: 0, bmi: 0, fat: 0 },
      within: { weight: 0, bmi: 0, fat: 0 }
    };

    effectiveMeasurements.forEach((m) => {
      const bundle = resolveTargetsBundleAtMs(getMeasurementEpochMs(m)) || scheduleTargets;
      const target = getWeightTarget(bundle);
      const weightKg = m.weight != null ? Number(m.weight) : null;
      if (weightKg != null && !Number.isNaN(weightKg)) {
        const s = checkWeightValue(weightKg, target);
        if (s === 'high') stats.above.weight += 1;
        else if (s === 'low') stats.below.weight += 1;
        else stats.within.weight += 1;
      }

      const fatRaw = m.fat ?? m.body_fat;
      const fatVal = fatRaw != null ? Number(fatRaw) : null;
      if (fatVal != null && !Number.isNaN(fatVal)) {
        const fatMin = Number(target.bodyFatMin || 14);
        const fatMax = Number(target.bodyFatMax || 27);
        if (fatVal > fatMax) stats.above.fat += 1;
        else if (fatVal < fatMin) stats.below.fat += 1;
        else stats.within.fat += 1;
      }

      const bmiVal = m.bmi != null ? Number(m.bmi) : null;
      if (bmiVal != null && !Number.isNaN(bmiVal)) {
        const bmiNormal = Number(target.bmiNormal || 18.5);
        const bmiObese = Number(target.bmiObese || 30);
        if (bmiVal >= bmiObese) stats.above.bmi += 1;
        else if (bmiVal < bmiNormal) stats.below.bmi += 1;
        else stats.within.bmi += 1;
      }
    });

    return stats;
  };

  const renderTargetBlock = (targetKind = 'below') => {
    const stats = calculateOverallTargetStats();
    const weightTotal = (stats.above.weight || 0) + (stats.below.weight || 0) + (stats.within.weight || 0);
    const bmiTotal = (stats.above.bmi || 0) + (stats.below.bmi || 0) + (stats.within.bmi || 0);
    const fatTotal = (stats.above.fat || 0) + (stats.below.fat || 0) + (stats.within.fat || 0);
    const titleMap = { below: 'Below target', above: 'Above target', within: 'Within target' };

    return (
      <div className="w-total-block">
        <div className="w-total-title">{titleMap[targetKind] || 'Below target'}</div>
        <div>Weight : {formatPercentCount(stats[targetKind]?.weight || 0, weightTotal)}</div>
        <div>Bmi : {formatPercentCount(stats[targetKind]?.bmi || 0, bmiTotal)}</div>
        <div>Fat : {formatPercentCount(stats[targetKind]?.fat || 0, fatTotal)}</div>
      </div>
    );
  };

  const periodList = useMemo(
    () => (periodNames.length > 0 ? periodNames : ['Morning', 'Afternoon', 'Evening']),
    [periodNames]
  );

  const overallTargetStats = useMemo(() => calculateOverallTargetStats(), [effectiveMeasurements, scheduleTargets, scheduleTargetSources, targetTimelineRev]);
  const weightTotalCount =
    (overallTargetStats.above.weight || 0) +
    (overallTargetStats.below.weight || 0) +
    (overallTargetStats.within.weight || 0);
  const fatTotalCount =
    (overallTargetStats.above.fat || 0) +
    (overallTargetStats.below.fat || 0) +
    (overallTargetStats.within.fat || 0);
  const bmiTotalCount =
    (overallTargetStats.above.bmi || 0) +
    (overallTargetStats.below.bmi || 0) +
    (overallTargetStats.within.bmi || 0);

  const getPeriodBadgeClass = (periodName) => {
    const n = normalizePeriodName(periodName);
    if (n.includes('wake')) return 'wakeup';
    if (n.includes('morning')) return 'morning';
    if (n.includes('noon') && !n.includes('after')) return 'noon';
    if (n.includes('afternoon')) return 'afternoon';
    if (n.includes('evening') || n.includes('night')) return 'evening';
    return 'other';
  };

  const getReadingTime = (measurement) => {
    const measureTime = getMeasurementWallClockTimeHms(measurement);
    return measureTime ? formatTime(measureTime) : '';
  };

  const sortReadingsNewestFirst = (arr) =>
    [...(arr || [])].sort((a, b) => {
      const timeA = getMeasurementWallClockTimeHms(a) || '00:00:00';
      const timeB = getMeasurementWallClockTimeHms(b) || '00:00:00';
      return timeB.localeCompare(timeA);
    });

  const resolveWeightPeriodName = (m) => {
    const ms = getMeasurementEpochMs(m);
    const bundle = resolveTargetsBundleAtMs(ms) || scheduleTargets;
    const periods = Array.isArray(bundle?.weightTimePeriod) ? bundle.weightTimePeriod : [];
    let period = resolvePeriodNameByIdOrLabel(periods, m) || m.period_name || m.period || '';
    if (!period || period === 'Unknown') {
      const mTime = getMeasurementWallClockTimeHms(m);
      if (mTime) {
        const byTime = resolvePeriodByTime(mTime, periods);
        if (byTime) period = byTime;
      }
    }
    return period;
  };

  const getDatePeriodMap = (date) => {
    const dateMeasurements = groupedMeasurements[date] || [];
    const byPeriod = {};
    dateMeasurements.forEach((m) => {
      const period = resolveWeightPeriodName(m);
      const matchedPeriod = periodList.find(
        (p) => normalizePeriodName(p) === normalizePeriodName(period || '')
      );
      if (!matchedPeriod) return;
      if (!byPeriod[matchedPeriod]) byPeriod[matchedPeriod] = [];
      byPeriod[matchedPeriod].push(m);
    });
    return byPeriod;
  };

  const getReadingLevelClass = (status) => {
    if (status === 'high' || status === 'obese' || status === BMI_CATEGORY.OBESE) return 'high';
    if (status === 'underweight' || status === BMI_CATEGORY.UNDERWEIGHT) return 'underweight';
    if (status === 'overweight' || status === BMI_CATEGORY.OVERWEIGHT) return 'overweight';
    if (status === 'low') return 'low';
    if (status === 'normal' || status === BMI_CATEGORY.NORMAL) return 'within';
    return '';
  };

  const getWeightReadingStatus = (measurement) => {
    const bundle = resolveTargetsBundleAtMs(getMeasurementEpochMs(measurement)) || scheduleTargets;
    const target = getWeightTarget(bundle);
    const weightKg = measurement.weight;
    if (weightKg == null || weightKg === '') return 'normal';
    return checkWeightValue(weightKg, target);
  };

  const getFatReadingStatus = (measurement) => {
    const bundle = resolveTargetsBundleAtMs(getMeasurementEpochMs(measurement)) || scheduleTargets;
    const target = getWeightTarget(bundle);
    const fat = measurement.fat ?? measurement.body_fat;
    if (fat == null || fat === '') return 'normal';
    const fatValue = Number(fat);
    if (Number.isNaN(fatValue)) return 'normal';
    const fatMin = target.bodyFatMin || 14;
    const fatMax = target.bodyFatMax || 27;
    if (fatValue > fatMax) return 'high';
    if (fatValue < fatMin) return 'low';
    return 'normal';
  };

  const getBmiReadingStatus = (measurement) => {
    const bundle = resolveTargetsBundleAtMs(getMeasurementEpochMs(measurement)) || scheduleTargets;
    const target = getWeightTarget(bundle);
    const bmi = measurement.bmi;
    if (bmi == null || bmi === '') return 'normal';
    return getBmiCategoryStatus(bmi, {
      bmiNormal: target.bmiNormal || 18.5,
      bmiOverweight: target.bmiOverweight || 25,
      bmiObese: target.bmiObese || 30
    });
  };

  const getWeightLb = (measurement) => {
    if (!measurement) return null;
    if (measurement.weight_lbs != null && measurement.weight_lbs !== '' && !Number.isNaN(Number(measurement.weight_lbs))) {
      return Number(measurement.weight_lbs);
    }
    const val = Number(measurement.weight);
    return Number.isFinite(val) && val > 0 ? val : null;
  };

  const dateRangeLabel = useMemo(() => {
    if (!sortedDates.length) return '';
    const newest = sortedDates[0];
    const oldest = sortedDates[sortedDates.length - 1];
    if (newest === oldest) return formatDate(newest);
    return `${formatDate(oldest)} – ${formatDate(newest)}`;
  }, [sortedDates]);

  const clearHoverHideTimer = () => {
    if (hoverHideTimerRef.current) {
      clearTimeout(hoverHideTimerRef.current);
      hoverHideTimerRef.current = null;
    }
  };

  const scheduleHoverHide = () => {
    clearHoverHideTimer();
    hoverHideTimerRef.current = setTimeout(() => {
      setHoverPreview(null);
      hoverHideTimerRef.current = null;
    }, 180);
  };

  const showHoverPreviewAt = (event, readings, periodName) => {
    if (!readings || readings.length < 2) return;
    clearHoverHideTimer();
    const sorted = sortReadingsNewestFirst(readings);
    const others = sorted.slice(1);
    const weightVals = sorted
      .map((r) => getWeightLb(r))
      .filter((n) => n != null && !Number.isNaN(Number(n)))
      .map((n) => Number(n));
    const avgWeight = weightVals.length
      ? (weightVals.reduce((a, b) => a + b, 0) / weightVals.length).toFixed(1)
      : null;
    const x = event.clientX + 12;
    const y = event.clientY + 12;
    setHoverPreview({
      x: Math.min(x, window.innerWidth - 220),
      y: Math.min(y, window.innerHeight - 120),
      title: `${String(periodName || 'Period').toUpperCase()} • EARLIER READINGS`,
      periodName,
      totalCount: sorted.length,
      averageLabel: avgWeight != null ? `${avgWeight} lb` : '—',
      readings: others
    });
  };

  useEffect(() => () => clearHoverHideTimer(), []);
  useEffect(() => {
    if (!showExportMenu) return undefined;
    const handleClickOutside = (event) => {
      if (!exportMenuRef.current?.contains(event.target)) {
        setShowExportMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showExportMenu]);

  // Get patient info for display
  const patient = patientData?.patient;
  const patientInfo = useMemo(() => {
    const patientNameLine = patient ? `${patient.last_name || ''}, ${patient.first_name || ''}`.trim() : 'Loading...';
    const patientDOB = patient?.date_of_birth
      ? `${formatDate(patient.date_of_birth)} (${new Date().getFullYear() - new Date(patient.date_of_birth).getFullYear()} years)`
      : 'N/A';
    const patientPhone = formatPhoneForDisplay(patient?.phone_number, 'N/A');
    const patientDevice = patientData?.device_serial || 'N/A';
    return { patientNameLine, patientDOB, patientPhone, patientDevice };
  }, [patient, patientData?.device_serial]);
  const { patientNameLine, patientDOB, patientPhone, patientDevice } = patientInfo;

  // Set breadcrumbs (must be after patientName is declared)
  useEffect(() => {
    if (patientData) {
      const fromDashboard = location.state?.fromDashboard;
      const rootCrumb = { label: 'Dashboard', path: '/dashboard' };
      const basePath = fromDashboard ? '/dashboard' : '/practices';

      updateBreadcrumbs([
        rootCrumb,
        { label: patientData?.patient?.practice_name || patientData?.practice?.practice_name || 'Practice', path: `${basePath}/${practiceId}`, state: { fromDashboard } },
        { label: 'Patients', path: `${basePath}/${practiceId}?tab=patient`, state: { fromDashboard } },

        { label: patientNameLine, path: `/practices/${practiceId}/patients/${patientId}`, state: { fromDashboard } },
        { label: 'Summary Of Measurement', path: null, active: true }
      ]);
    }
  }, [patientData, practiceId, patientId, patientNameLine, updateBreadcrumbs, location.state]);


  const reportTitle = `Weight Report - ${patientNameLine}`;
  const exportFilename = `Weight-Report-${patientNameLine.replace(/\s+/g, '-')}-${new Date().toISOString().split('T')[0]}.pdf`;

  const handlePrint = () => {
    try {
      const el = document.getElementById('w-report-content');
      printElement(el, reportTitle);
    } catch (e) {
      toast.error(e?.message || 'Print failed');
    }
  };

  const handlePdfExport = async () => {
    if (effectiveMeasurements.length === 0) {
      toast.warning('No measurement data to export');
      return;
    }
    try {
      setIsExporting(true);
      exportWeightTablesPdf({
        filename: exportFilename,
        title: reportTitle,
        patient: { name: patientNameLine, dob: patientDOB, phone: patientPhone, device: patientDevice },
        dateBuckets,
        periodList,
        groupedMeasurements,
        resolvePeriodForMeasurement: (date) => getDatePeriodMap(date),
        formatDate,
        formatTime,
        calculatePeriodStats,
        effectiveMeasurements,
        calculateOverallTargetStats,
        formatPercentCount
      });
      toast.success('Exported to PDF');
      patientService.logPatientExport(practiceId, patientId, { format: 'pdf', source: 'Weight' });
    } catch (e) {
      toast.error(e?.message || 'Export failed');
    } finally {
      setIsExporting(false);
    }
  };

  const handleCsvExport = () => {
    if (effectiveMeasurements.length === 0) {
      toast.warning('No measurement data to export');
      return;
    }
    const rows = [['Date', 'Period', 'Weight (lb)', 'Body Fat (%)', 'BMI', 'Measure Time']];
    sortedDates.forEach((date) => {
      const byPeriod = getDatePeriodMap(date);
      periodList.forEach((periodName) => {
        const readings = sortReadingsNewestFirst(byPeriod[periodName] || []);
        readings.forEach((reading) => {
          const weightLb = getWeightLb(reading);
          const fat = reading.fat ?? reading.body_fat ?? '';
          const bmi = reading.bmi ?? '';
          const measureTime = getMeasurementWallClockTimeHms(reading);
          rows.push([
            formatDate(date),
            periodName,
            weightLb == null ? '' : Number(weightLb).toFixed(1),
            fat === '' ? '' : Number(fat).toFixed(1),
            bmi === '' ? '' : Number(bmi).toFixed(1),
            measureTime ? formatTime(measureTime) : ''
          ]);
        });
      });
    });
    downloadBlob(new Blob([toCsv(rows)], { type: 'text/csv;charset=utf-8;' }), exportFilename.replace(/\.pdf$/i, '.csv'));
    toast.success('Exported to CSV');
    patientService.logPatientExport(practiceId, patientId, { format: 'csv', source: 'Weight' });
  };

  const defaultEmailSubject = `Weight Report - ${patientNameLine} (${new Date().toLocaleDateString()})`;
  const defaultEmailContent = `Weight Report\nPatient: ${patientNameLine} (DOB: ${patientDOB})\nGenerated: ${new Date().toLocaleString()}\nTotal Readings: ${effectiveMeasurements.length}`;
  // Keep controls visible on tab switch (same UX as BP screen).
  // Table/summary have their own loading skeletons via queryLoading.
  const showInitialSkeleton = false;

  return (
    <div className="weight-page">
      {!useSharedControls && practiceIsLocked && (
        <div className="practice-locked-measurement-banner" role="status">
          This practice is locked. Historical readings remain available for up to 30 days. New measurements and
          follow-ups are not allowed.
        </div>
      )}
      <div id="w-report-content" style={{ padding: '0px' }}>
      {showInitialSkeleton ? (
        <div className="w-skeleton-page">
          <div className="w-skeleton-block w-skeleton-layout">
            <div className="w-skeleton-line w-w-40" />
            <div className="w-skeleton-line w-w-80" />
            <div className="w-skeleton-line w-w-60" />
          </div>
          <div className="w-skeleton-block w-skeleton-query">
            <div className="w-skeleton-line w-w-40" />
            <div className="w-skeleton-line w-w-80" />
            <div className="w-skeleton-line w-w-60" />
          </div>
          <div className="w-skeleton-block w-skeleton-table">
            <div className="w-table-skeleton">
              <div className="w-table-skel-head" />
              <div className="w-table-skel-row" />
              <div className="w-table-skel-row" />
              <div className="w-table-skel-row" />
            </div>
          </div>
          <div className="w-skeleton-block w-skeleton-summary">
            <div className="w-table-skeleton">
              <div className="w-table-skel-head" />
              <div className="w-table-skel-row" />
              <div className="w-table-skel-row" />
            </div>
          </div>
        </div>
      ) : (
      <>
      
      {!useSharedControls && (
      <div className="bp-section bp-section--controls">
          <div className="bp-controls-header">
            <div className="bp-controls-header-left">
              <h2 className="bp-section-title">Measurement Controls</h2>
              <div className="bp-patient-chips">
                <div className="bp-chip">
                  <div className="chip-text">
                    <span className="chip-label">Name</span>
                    <span className="chip-value">{patientNameLine}</span>
                  </div>
                </div>
                <div className="bp-chip">
                  <div className="chip-text">
                    <span className="chip-label">DOB</span>
                    <span className="chip-value">{patientDOB}</span>
                  </div>
                </div>
                <div className="bp-chip">
                  <div className="chip-text">
                    <span className="chip-label">Phone</span>
                    <span className="chip-value">{patientPhone}</span>
                  </div>
                </div>
                <div className="bp-chip">
                  <div className="chip-text">
                    <span className="chip-label">Device</span>
                    <span className="chip-value">{patientDevice}</span>
                  </div>
                </div>
              </div>
            </div>
            {!practiceIsLocked && (
              <div className="bp-controls-header-right" data-export-ignore="true">
                <div className="bp-timer-block">
                  <div className="bp-timer-text">
                    <span className="bp-timer-label">Active Session</span>
                    <span className="bp-timer-value">{formatTimer(timer)}</span>
                  </div>
                  <div className="bp-timer-actions">
                    <button
                      type="button"
                      className={`bp-timer-btn bp-timer-toggle ${isRunning ? 'running' : 'paused'}`}
                      onClick={() => setIsRunning((prev) => !prev)}
                      title={isRunning ? 'Pause Timer' : 'Start Timer'}
                      aria-label={isRunning ? 'Pause Timer' : 'Start Timer'}
                    >
                      <FontAwesomeIcon icon={isRunning ? faPause : faPlay} />
                    </button>
                    <button
                      type="button"
                      className="bp-timer-btn bp-timer-close"
                      onClick={() => {
                        setIsRunning(false);
                        setTimer(0);
                      }}
                      title="Reset Timer"
                      aria-label="Reset Timer"
                    >
                      <FontAwesomeIcon icon={faTimes} />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className="bp-controls-nav" data-export-ignore="true">
            <VitalsTabs
              practiceId={practiceId}
              patientId={patientId}
              vitals={patientVitals}
              active="weight"
              mode="measurement"
              className="bp-controls-vitals-tabs"
            />
            {!practiceIsLocked && (
              <button
                className="bp-add-followup-btn"
                type="button"
                onClick={() => {
                  setIsRunning(false);
                  setShowFollowUpModal(true);
                }}
              >
                Add Follow Up
              </button>
            )}
          </div>
        </div>
      )}

      {/* Query Section */}
      <div className="bp-section bp-section--query" data-export-ignore="true">
        <div className="bp-section-title-row">
          <h2 className="bp-section-title">Query Period</h2>
        </div>
        <div className="bp-card">
          <div className="bp-card-body">
            <div className="bp-query-body">
              <div>
                <label className={`bp-option-card${queryType === 'dateRange' ? ' is-selected' : ''}`}>
                  <input
                    type="radio"
                    name="wt-queryType"
                    value="dateRange"
                    checked={queryType === 'dateRange'}
                    onChange={() => handleQueryTypeChange('dateRange')}
                  />
                  <span className="bp-option-label">Date Range</span>
                  <select
                    className="bp-dropdown"
                    value={dateRange}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => {
                      const value = e.target.value;
                      setDateRange(value);
                      if (String(value).toUpperCase() === 'ALL') {
                        setFromDate('');
                        setToDate('');
                      }
                    }}
                    disabled={queryType !== 'dateRange'}
                  >
                    <option>Last 30 days</option>
                    <option>Last 7 days</option>
                    <option>Last 14 days</option>
                    <option>Last 60 days</option>
                    <option>Last 90 days</option>
                    <option>All</option>
                  </select>
                </label>
                <label className={`bp-option-card${queryType === 'from' ? ' is-selected' : ''}`}>
                  <input
                    type="radio"
                    name="wt-queryType"
                    value="from"
                    checked={queryType === 'from'}
                    onChange={() => handleQueryTypeChange('from')}
                  />
                  <span className="bp-option-label">From / To</span>
                  <div className="bp-date-inputs" onClick={(e) => e.stopPropagation()}>
                    <AppDateInput
                      className="bp-date-input"
                      value={fromDate}
                      max={getMeasurementQueryMaxDate()}
                      onChange={(nextFrom) => {
                        const next = applyMeasurementFromDateChange(nextFrom, toDate);
                        setFromDate(next.fromDate);
                        setToDate(next.toDate);
                      }}
                      disabled={queryType !== 'from'}
                    />
                    <span className="bp-date-separator">to</span>
                    <AppDateInput
                      className="bp-date-input"
                      value={toDate}
                      min={fromDate || undefined}
                      max={getMeasurementQueryMaxDate()}
                      onChange={(nextTo) => setToDate(sanitizeMeasurementToDate(nextTo, fromDate))}
                      disabled={queryType !== 'from'}
                    />
                  </div>
                </label>
              </div>
              <div>
                <div className="bp-sort-block">
                  <label htmlFor="wt-sort-select">Sort Data By</label>
                  <select
                    id="wt-sort-select"
                    className="bp-dropdown"
                    style={{ width: '100%' }}
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                  >
                    <option>Weekly average</option>
                    <option>Daily average</option>
                    <option>Monthly average</option>
                    <option>Date</option>
                  </select>
                </div>
                <div className="bp-action-buttons-row">
                  <button className="bp-action-btn primary" type="button" onClick={handleQuery} disabled={isQueryDisabled}>
                    Query
                  </button>
                  <button className="bp-action-btn" type="button" onClick={handleReset} disabled={isResetDisabled}>
                    Reset
                  </button>
                  {!practiceIsLocked && (
                    <button
                      className={`bp-action-btn${!canAddThisVital ? ' bp-action-btn--muted' : ''}`}
                      type="button"
                      disabled={!canAddThisVital}
                      title={!canAddThisVital ? PRACTICE_VITAL_ADD_DISABLED_TITLE : undefined}
                      onClick={handleOpenAddModal}
                    >
                      Add Data
                    </button>
                  )}
                  <button className="bp-action-btn" type="button" onClick={handlePrint}>
                    Print
                  </button>
                  <div className="bp-export-container" ref={exportMenuRef}>
                    <button
                      className="bp-action-btn"
                      type="button"
                      onClick={() => setShowExportMenu((prev) => !prev)}
                      disabled={isExporting}
                      aria-haspopup="menu"
                      aria-expanded={showExportMenu}
                    >
                      {isExporting ? 'Exporting...' : 'Export'} <span className="bp-export-caret">▾</span>
                    </button>
                    <div className={`bp-export-menu${showExportMenu ? ' show' : ''}`} role="menu">
                      <button
                        type="button"
                        role="menuitem"
                        onClick={() => {
                          setShowExportMenu(false);
                          handlePdfExport();
                        }}
                      >
                        PDF
                      </button>
                      <button
                        type="button"
                        role="menuitem"
                        onClick={() => {
                          setShowExportMenu(false);
                          handleCsvExport();
                        }}
                      >
                        CSV
                      </button>
                    </div>
                  </div>
                  <button className="bp-action-btn" type="button" onClick={() => setShowEmailModal(true)}>
                    Email
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Daily Measurement Log Table */}
      <div className="bp-section">
        <div className="bp-section-title-row">
          <h2 className="bp-section-title">Weight Measurements</h2>
          {dateRangeLabel && (
            <span className="bp-section-meta">{dateRangeLabel} · hover a cell to see every reading</span>
          )}
        </div>
        <div className="bp-card bp-card--table">
          <div className="bp-card-body bp-card-body--flush">
            {queryLoading ? (
              <div className="bp-empty-state">
                <p className="bp-empty-title">Loading measurements…</p>
              </div>
            ) : effectiveMeasurements.length === 0 ? (
              <div className="bp-empty-state">
                <p className="bp-empty-title">No measurement data</p>
                <p className="bp-empty-sub">No measurement data available for the selected period.</p>
              </div>
            ) : (
              <div className="bphr-grid-container">
                <table className="bphr-table">
                  <colgroup>
                    <col style={{ width: '100px' }} />
                    {periodList.map((periodName) => (
                      <col key={periodName} style={{ width: '140px' }} />
                    ))}
                  </colgroup>
                  <thead>
                    <tr>
                      <th rowSpan={2} className="datehd">Date</th>
                      {periodList.map((periodName) => {
                        const periodInfo = getPeriodInfo(periodName);
                        const timeRange =
                          periodInfo?.startTimeDisplay && periodInfo?.endTimeDisplay
                            ? `${periodInfo.startTimeDisplay}–${periodInfo.endTimeDisplay}`
                            : '';
                        return (
                          <th key={periodName} className="grp">
                            {periodName}
                            {timeRange ? <span className="hds">{timeRange}</span> : null}
                          </th>
                        );
                      })}
                    </tr>
                    <tr>
                      {periodList.map((periodName) => (
                        <th key={`${periodName}-sub`} className="sub">
                          Weight <span className="sub-unit">(lbs)</span>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {sortedDates.map((date) => {
                      const byPeriod = getDatePeriodMap(date);
                      return (
                        <tr key={date} className="reading-row">
                          <td className="cell date-col">{formatDate(date)}</td>
                          {periodList.map((periodName) => {
                            const readings = sortReadingsNewestFirst(byPeriod[periodName] || []);
                            if (!readings.length) {
                              return (
                                <td key={periodName} className="cell metric">
                                  <span className="empty">—</span>
                                </td>
                              );
                            }
                            return (
                              <td key={periodName} className="cell metric">
                                {readings.map((r, idx) => {
                                  const weightClass = getReadingLevelClass(getWeightReadingStatus(r));
                                  const fatClass = getReadingLevelClass(getFatReadingStatus(r));
                                  const bmiClass = getReadingLevelClass(getBmiReadingStatus(r));
                                  const weightLb = getWeightLb(r);
                                  const fat = r.fat ?? r.body_fat;
                                  const bmi = r.bmi;
                                  const timeStr = getReadingTime(r);
                                  return (
                                    <React.Fragment key={r.id || idx}>
                                      {idx > 0 && <div className="dash" />}
                                      <div className="bphr-metric">
                                        <div className="bphr-metric-value">
                                          <span className={weightClass}>
                                            {weightLb != null ? weightLb : '—'}
                                          </span>
                                        </div>
                                        {fat != null && fat !== '' ? (
                                          <div className={`bphr-metric-label ${fatClass}`} style={{ fontWeight: 600 }}>
                                            Fat {Number(fat).toFixed(1)}
                                          </div>
                                        ) : null}
                                        {bmi != null && bmi !== '' ? (
                                          <div className={`bphr-metric-label ${bmiClass}`} style={{ fontWeight: 600 }}>
                                            BMI {Number(bmi).toFixed(1)}
                                          </div>
                                        ) : null}
                                        {timeStr ? <div className="bphr-metric-label">{timeStr}</div> : null}
                                      </div>
                                    </React.Fragment>
                                  );
                                })}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Summary Statistics Table */}
      <div className="bp-section">
        <div className="bp-section-title-row">
          <h2 className="bp-section-title">Summary Of Measurement</h2>
          <div className="bp-target-legend">
            <span><span className="dot above" />Above</span>
            <span><span className="dot within" />Within</span>
            <span><span className="dot below" />Below</span>
          </div>
        </div>
        <div className="bp-card bp-card--table">
          <div className="bp-card-body bp-card-body--flush">
            {queryLoading ? (
              <div className="bp-empty-state"><p className="bp-empty-title">Loading summary…</p></div>
            ) : (
              <>
                <table className="bp-simple-table">
                  <thead>
                    <tr>
                      <th>Period</th>
                      <th className="num">Weight (avg / max / min)</th>
                      <th className="num">Body Fat (avg / max / min)</th>
                      <th className="num">BMI (avg / max / min)</th>
                      <th>Target Range</th>
                    </tr>
                  </thead>
                  <tbody>
                    {periodList.map((periodName) => {
                      const periodInfo = getPeriodInfo(periodName);
                      const timeRange =
                        periodInfo?.startTimeDisplay && periodInfo?.endTimeDisplay
                          ? `${periodInfo.startTimeDisplay}–${periodInfo.endTimeDisplay}`
                          : '';
                      const stats = calculatePeriodStats(effectiveMeasurements, periodName);
                      const above = stats.above?.weight || 0;
                      const within = stats.within?.weight || 0;
                      const below = stats.below?.weight || 0;
                      const totalSeg = above + within + below;
                      const fmt = (v) => (v != null && !Number.isNaN(v) ? Number(v).toFixed(1) : '—');
                      const hasData = (stats.count || 0) > 0;
                      return (
                        <tr key={periodName}>
                          <td>
                            <span className={`bp-period-badge ${getPeriodBadgeClass(periodName)}`}>
                              <span className="pb-name">{periodName}</span>
                              {timeRange ? <span className="pb-time">{timeRange}</span> : null}
                            </span>
                          </td>
                          {hasData ? (
                            <>
                              <td className="num">
                                <div className="bp-mini-minmax">
                                  <span className="mm-avg">{fmt(stats.avg?.weight)}</span>{' '}
                                  <span className="mm-range">({fmt(stats.min?.weight)}–{fmt(stats.max?.weight)})</span>
                                </div>
                              </td>
                              <td className="num">
                                <div className="bp-mini-minmax">
                                  <span className="mm-avg">{fmt(stats.avg?.fat)}</span>{' '}
                                  <span className="mm-range">({fmt(stats.min?.fat)}–{fmt(stats.max?.fat)})</span>
                                </div>
                              </td>
                              <td className="num">
                                <div className="bp-mini-minmax">
                                  <span className="mm-avg">{fmt(stats.avg?.bmi)}</span>{' '}
                                  <span className="mm-range">({fmt(stats.min?.bmi)}–{fmt(stats.max?.bmi)})</span>
                                </div>
                              </td>
                            </>
                          ) : (
                            <td className="num bp-no-data-inline" colSpan={3}>No readings</td>
                          )}
                          <td className="bp-target-bar-cell">
                            <div className="bp-target-seg-bar">
                              {totalSeg > 0 ? (
                                <>
                                  <div className="seg above" style={{ width: `${(above / totalSeg) * 100}%` }} />
                                  <div className="seg within" style={{ width: `${(within / totalSeg) * 100}%` }} />
                                  <div className="seg below" style={{ width: `${(below / totalSeg) * 100}%` }} />
                                </>
                              ) : (
                                <div className="seg" style={{ width: '100%', background: '#F1F0EC' }} />
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <div className="bp-total-grid">
                  <div className="bp-total-card above">
                    <div className="bp-total-title">Above target</div>
                    <div className="stat-line">Weight: {formatPercentCount(overallTargetStats.above.weight, weightTotalCount)}</div>
                    <div className="stat-line">Body Fat: {formatPercentCount(overallTargetStats.above.fat, fatTotalCount)}</div>
                    <div className="stat-line">BMI: {formatPercentCount(overallTargetStats.above.bmi, bmiTotalCount)}</div>
                  </div>
                  <div className="bp-total-card within">
                    <div className="bp-total-title">Within target</div>
                    <div className="stat-line">Weight: {formatPercentCount(overallTargetStats.within.weight, weightTotalCount)}</div>
                    <div className="stat-line">Body Fat: {formatPercentCount(overallTargetStats.within.fat, fatTotalCount)}</div>
                    <div className="stat-line">BMI: {formatPercentCount(overallTargetStats.within.bmi, bmiTotalCount)}</div>
                  </div>
                  <div className="bp-total-card below">
                    <div className="bp-total-title">Below target</div>
                    <div className="stat-line">Weight: {formatPercentCount(overallTargetStats.below.weight, weightTotalCount)}</div>
                    <div className="stat-line">Body Fat: {formatPercentCount(overallTargetStats.below.fat, fatTotalCount)}</div>
                    <div className="stat-line">BMI: {formatPercentCount(overallTargetStats.below.bmi, bmiTotalCount)}</div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Add Weight Modal */}
      {!practiceIsLocked && canAddThisVital && showAddModal && (
        <AddWForm
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
          practiceId={practiceId}
          patientId={patientId}
          onSuccess={handleAddDataSuccess}
          initialScheduleTargets={scheduleTargets}
        />
      )}

      {!useSharedControls && !practiceIsLocked && showFollowUpModal && (
        <AddFollowUpModal
          isOpen={showFollowUpModal}
          onClose={() => setShowFollowUpModal(false)}
          practiceId={practiceId}
          patientId={patientId}
          patientName={patientNameLine}
          initialTimerValue={timer}
          onTimerSync={(seconds) => {
            setTimer(Math.max(0, Number(seconds) || 0));
            setIsRunning(true);
          }}
          onSuccess={() => {}}
        />
      )}
      </>
      )}
      </div>{/* end w-report-content */}

      <EmailModal
        isOpen={showEmailModal}
        onClose={() => setShowEmailModal(false)}
        defaultSubject={defaultEmailSubject}
        defaultContent={defaultEmailContent}
        onSend={async (emailData) => {
          try {
            setEmailSending(true);
            const res = await sendReportEmail(emailData, { event_id: 11 });
            if (!res.success) throw new Error(res.error || res.message);
            toast.success('Email queued successfully');
            patientService.logPatientExport(practiceId, patientId, {
              format: 'email',
              source: 'Weight'
            });
            setShowEmailModal(false);
          } catch (e) {
            toast.error(e?.message || 'Failed to send email');
          } finally {
            setEmailSending(false);
          }
        }}
      />
    </div>
  );
};

export default Weight;

